import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  FilterGroupingEnum,
  Site,
  SiteFilterInputObject,
  CompanyPlanUpgradeFeatureEnum,
} from 'graphql-common';
import Table from '@lib/components/Table/Table';
import Search from '@lib/components/Search/Search';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import FeatureLockState from '@lib/components/FeatureLockState';
import Trans from '@lib/components/Trans/Trans';
import { IconButtonTypes } from '@lib/components/IconButton/IconButton';
import { PlanState } from '@lib/enums/plan';
import { TableAction } from '@lib/components/Table/types';
import useTabListState from '@lib/components/Table/utils/useTabListState';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import useSitesQueryHook from 'utils/fetch/hooks/useSitesQueryHook';
import { APP_URLS } from 'constants/urls';
import getSitesColumns from './utils/getSitesColumns';

interface SitesTabProps {
  documentId: string;
}

export default function SitesTab({ documentId }: SitesTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { listState, params, searchQuery, onSearchChange } = useTabListState();
  const { getUserAccessByCompany, handleOpenPlanModal }: ContextProps =
    useOutletContext();
  const isPlanLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  const additionalFilters: SiteFilterInputObject[] = useMemo(
    () => [
      {
        documentationsId: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: documentId },
          filterGroup: 'documentationsId',
        },
      },
    ],
    [documentId],
  );

  const { collection, firstLoading, loading, totalCount } = useSitesQueryHook({
    additionalFilters,
    paginationParams: params,
    searchQuery,
    skip: isPlanLocked,
  });

  const onViewHandler = useCallback(
    (item: Site) => {
      navigate(
        APP_URLS.dashboard.sites.view.getDynamicPath({
          pathParts: { id: item.id },
        }),
      );
    },
    [navigate],
  );

  const columns = useMemo(() => getSitesColumns({ t }), [t]);

  const tableActions: TableAction<Site>[] = useMemo(
    () => [
      {
        id: 'view-action',
        icon: 'visibility',
        onClick: onViewHandler,
        type: IconButtonTypes.PrimaryOutlined,
        predictorFnTooltipProps: () => ({ body: t('view') }),
      },
    ],
    [onViewHandler, t],
  );

  if (isPlanLocked) {
    return (
      <FeatureLockState
        title={t('unlock-site-level-document-access')}
        text={<Trans i18nKey="unlock-site-level-document-access-text" />}
        onUpgrade={() =>
          handleOpenPlanModal(PlanState.Locked, [
            CompanyPlanUpgradeFeatureEnum.Documentation,
          ])
        }
      />
    );
  }

  if (firstLoading) {
    return <CircleLoader />;
  }

  const emptyState = (
    <ListEmptyState
      title={t('no-linked-sites')}
      text={t('no-linked-sites-text')}
    />
  );

  return (
    <>
      {((!!totalCount && !!collection.length) || searchQuery) && (
        <Search
          placeholder={t('document-sites-search-placeholder')}
          value={searchQuery}
          onChange={onSearchChange}
          useSearchParam={false}
        />
      )}
      <Table
        actions={tableActions}
        columns={columns}
        counterText={t('site-plural', { count: totalCount })}
        data={collection}
        emptyState={emptyState}
        id="documentSites"
        loading={loading}
        orderingState={listState}
        paginationState={listState}
        totalEntries={totalCount}
      />
    </>
  );
}
